(() => {
  const CHAT_SELECTORS = [
    'iframe#ShopifyChat',
    'iframe[id*="ShopifyChat"]',
    'iframe[name*="ShopifyChat"]',
    'iframe#dummy-chat-button-iframe',
    'iframe[name="dummy-chat-button-iframe"]',
    'iframe[id*="chat-button" i]',
    'iframe[title*="chat" i]',
    'iframe[src*="shopifychat" i]',
    'iframe[src*="chat.shopify" i]',
    'iframe[src*="inbox" i]',
    '#ShopifyChat',
    '[id*="ShopifyChat"]',
    '#shopify-chat-dummy',
    '[data-shopify-chat]',
    '[id*="shopify-chat" i]',
    '[class*="shopify-chat" i]',
    '[id*="inbox" i]',
    '[class*="inbox" i]',
    '[data-testid*="chat" i]',
    'shopify-inbox',
    'shopify-chat',
  ];

  const CANDIDATE_SELECTOR = [
    'iframe',
    'button',
    '[role="button"]',
    '[aria-label]',
    '[title]',
    '[data-testid]',
    '[id*="chat" i]',
    '[class*="chat" i]',
    '[id*="inbox" i]',
    '[class*="inbox" i]',
    'shopify-inbox',
    'shopify-chat',
  ].join(',');

  let dismissed = false;
  let closeButton = null;
  let scheduled = false;

  const getRoots = () => {
    const roots = [document];
    const seen = new Set(roots);

    for (let index = 0; index < roots.length; index += 1) {
      const root = roots[index];
      root.querySelectorAll?.('*').forEach((element) => {
        if (element.shadowRoot && !seen.has(element.shadowRoot)) {
          seen.add(element.shadowRoot);
          roots.push(element.shadowRoot);
        }
      });
    }

    return roots;
  };

  const queryAllDeep = (selector) => {
    const results = new Set();
    getRoots().forEach((root) => {
      try {
        root.querySelectorAll(selector).forEach((element) => results.add(element));
      } catch (_error) {
        // A third-party shadow root can reject a selector during initialization.
      }
    });
    return Array.from(results);
  };

  const getIdentity = (element) => {
    const values = [
      element.tagName,
      element.id,
      typeof element.className === 'string' ? element.className : '',
      element.getAttribute?.('name'),
      element.getAttribute?.('title'),
      element.getAttribute?.('aria-label'),
      element.getAttribute?.('data-testid'),
      element.getAttribute?.('src'),
      element.textContent?.trim().slice(0, 160),
      element.shadowRoot?.textContent?.trim().slice(0, 160),
    ];
    return values.filter(Boolean).join(' ').toLowerCase();
  };

  const getPositionStyle = (element) => {
    const style = window.getComputedStyle(element);
    if (style.position === 'fixed' || style.position === 'absolute') return style.position;

    let parent = element.parentElement;
    for (let depth = 0; parent && depth < 4; depth += 1, parent = parent.parentElement) {
      const parentStyle = window.getComputedStyle(parent);
      if (parentStyle.position === 'fixed' || parentStyle.position === 'absolute') return parentStyle.position;
    }
    return style.position;
  };

  const isCompactCornerWidget = (element) => {
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    const positioned = getPositionStyle(element);

    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      Number(style.opacity || 1) > 0 &&
      (positioned === 'fixed' || positioned === 'absolute') &&
      rect.width >= 34 &&
      rect.height >= 30 &&
      rect.width <= 520 &&
      rect.height <= 420 &&
      rect.right > window.innerWidth - 560 &&
      rect.bottom > window.innerHeight - 420
    );
  };

  const isVisible = (element) => {
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      Number(style.opacity || 1) > 0 &&
      rect.width >= 34 &&
      rect.height >= 30 &&
      rect.width <= 520 &&
      rect.height <= 420 &&
      rect.right > window.innerWidth - 560 &&
      rect.bottom > window.innerHeight - 420
    );
  };

  const findChatTargets = () => {
    const targets = new Set();

    CHAT_SELECTORS.forEach((selector) => {
      queryAllDeep(selector).forEach((element) => {
        if (isVisible(element)) targets.add(element);
      });
    });

    queryAllDeep(CANDIDATE_SELECTOR).forEach((element) => {
      const identity = getIdentity(element);
      const looksLikeChat =
        identity.includes('shopifychat') ||
        identity.includes('shopify chat') ||
        identity.includes('shopify-inbox') ||
        identity.includes('shopify inbox') ||
        identity.includes('chat with us') ||
        identity.includes('chat_with_us') ||
        /(^|\s)chat($|\s)/.test(identity) ||
        identity.includes('chat bubble') ||
        identity.includes('chat-button') ||
        identity.includes('chat_button');

      if (looksLikeChat && isCompactCornerWidget(element)) targets.add(element);
    });

    return Array.from(targets);
  };

  const hideElement = (element) => {
    element.dataset.coveChatDismissed = 'true';
    element.style.setProperty('display', 'none', 'important');
    element.setAttribute('aria-hidden', 'true');

    let parent = element.parentElement;
    for (let depth = 0; parent && parent !== document.body && depth < 4; depth += 1, parent = parent.parentElement) {
      const parentStyle = window.getComputedStyle(parent);
      const childRect = element.getBoundingClientRect();
      const parentRect = parent.getBoundingClientRect();
      const closelyWrapsChild =
        parentRect.width <= Math.max(childRect.width + 60, 560) &&
        parentRect.height <= Math.max(childRect.height + 60, 460) &&
        parentRect.width <= 560 &&
        parentRect.height <= 460;

      if (closelyWrapsChild && (parentStyle.position === 'fixed' || parentStyle.position === 'absolute')) {
        parent.dataset.coveChatDismissed = 'true';
        parent.style.setProperty('display', 'none', 'important');
        parent.setAttribute('aria-hidden', 'true');
        break;
      }
    }
  };

  const dismissChatForThisPage = () => {
    dismissed = true;
    findChatTargets().forEach(hideElement);
    closeButton?.remove();
    closeButton = null;
  };

  const ensureButton = () => {
    if (closeButton?.isConnected) return closeButton;

    closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'cove-chat-dismiss';
    closeButton.setAttribute('aria-label', 'Close chat for this page');
    closeButton.setAttribute('title', 'Close chat');
    closeButton.innerHTML = '<span aria-hidden="true">×</span>';
    closeButton.addEventListener('click', dismissChatForThisPage);
    document.body.appendChild(closeButton);
    return closeButton;
  };

  const positionButton = () => {
    scheduled = false;

    if (dismissed) {
      findChatTargets().forEach(hideElement);
      return;
    }

    const target = findChatTargets().sort((a, b) => {
      const ar = a.getBoundingClientRect();
      const br = b.getBoundingClientRect();
      return ar.width * ar.height - br.width * br.height;
    })[0];

    if (!target) {
      if (closeButton) closeButton.hidden = true;
      return;
    }

    const rect = target.getBoundingClientRect();
    const button = ensureButton();
    const buttonSize = 24;
    const left = Math.min(window.innerWidth - buttonSize - 5, Math.max(5, rect.right - buttonSize * 0.62));
    const top = Math.min(window.innerHeight - buttonSize - 5, Math.max(5, rect.top - buttonSize * 0.42));

    if (document.body.lastElementChild !== button) document.body.appendChild(button);
    button.hidden = false;
    button.style.left = `${left}px`;
    button.style.top = `${top}px`;
  };

  const schedulePosition = () => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(positionButton);
  };

  const start = () => {
    const observer = new MutationObserver(schedulePosition);
    observer.observe(document.documentElement, { childList: true, subtree: true, attributes: true });

    schedulePosition();
    window.addEventListener('resize', schedulePosition, { passive: true });
    window.addEventListener('scroll', schedulePosition, { passive: true });
    window.setInterval(schedulePosition, 650);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
